import React, { useEffect, useState } from 'react'
import { Button, Col, Label, Modal, Row, Table, UncontrolledTooltip } from 'reactstrap'
import VariableForm from './VariableForm'
import { Tbody, Thead } from 'react-super-responsive-table'

const formatInfoItem = (item) => {
    if (typeof item === 'string') return item
    if (item.name && item.loadTypes?.length) {
        const ltNames = item.loadTypes.map(lt => lt.name).join(', ')
        return `${item.name} (${ltNames})`
    }
    return item.name || ''
}

const formatInfoList = (information) => {
    if (!information?.length) return ''
    return information.map(formatInfoItem).join(' | ')
}

// Tooltip ids cannot be built from the var name: OpenDock field names carry the facility's own
// punctuation, and reactstrap resolves a tooltip target with querySelectorAll. Counted rather
// than random, and not useId(), whose ':r0:' is itself an invalid selector.
let tablesMounted = 0

const VarData = (props) => {

    const [vars, setVars] = useState(props.vars || [])
    // One prefix per mounted table, so two tables on a page cannot share a tooltip id.
    const [infoIdPrefix] = useState(() => `varinfo${tablesMounted++}`)
    const [showAddForm, setShowAddForm] = useState(false)
    const [editingVar, setEditingVar] = useState(null)
    const [editingVarIndex, setEditingVarIndex] = useState(null)

    const classNameForLabel = `col-sm-${props.labelWidth} classNameForLabel`
    const dataWidth = `${12-props.labelWidth}`

    const addVar = () => {
        setEditingVar(null)
        setEditingVarIndex(null)
        setShowAddForm(true)
    }

    const togAddForm = () => {
        setShowAddForm(!showAddForm)
    }

    useEffect(() => {
        setVars(props.vars || [])
    }, [props.vars])

    const addNewVar = (val) => {
        if (editingVar) {
            var variables = [...vars]
            variables[editingVarIndex] = val
            setVars(variables)
            props.setVars(variables)

        } else {
            if (vars) {
                var variables = [...vars]
                variables.push(val)
                setVars(variables)
                props.setVars(variables)
            } else {
                var variables = []
                variables.push(val)
                setVars(variables)
                props.setVars(variables)
            }
        }

        setEditingVar(null)
        setEditingVarIndex(null)
        togAddForm()

    }

    const editVar = (val, index) => {
        setEditingVar(val)
        setEditingVarIndex(index)
        setShowAddForm(true)
    }

    const removeVar = (index) => {
        var newvars = vars?.filter((item, idx) => idx !== index)
        setVars(newvars)
        props.setVars(newvars)
    }



    return (
        <>
            <Modal isOpen={showAddForm}
                toggle={togAddForm}
                size="xl"
                centered
                className="opendock-variable-modal"
            >
                <VariableForm closeModal={togAddForm} onSave={addNewVar} varData={editingVar} vars={vars} isNew={props.isNew} isEdit={props.isEdit} />
            </Modal>
            <Row>
                <Label
                    htmlFor="desc"
                    className={classNameForLabel}
                >
                    Variables
                </Label>
                <Col md={dataWidth}>
                    {(!vars || vars.length == 0) && !props.isEdit && <div className='text-muted'>{"No variables added."}</div>}
                    {vars?.length > 0 &&
                        <Table>
                            <Thead>
                                <tr>
                                    <th style={{ border: 'none' }}>
                                        Label
                                    </th>
                                    <th style={{ border: 'none' }}>
                                        Name
                                    </th>
                                    <th style={{ border: 'none' }}>
                                        Type
                                    </th>
                                    <th style={{ border: 'none' }}>
                                        Value
                                    </th>
                                    <th style={{ border: 'none' }}>
                                        Required
                                    </th>
                                    {props.extraColumn && (
                                        <th style={{ border: 'none' }}>
                                            {props.extraColumn.header}
                                        </th>
                                    )}
                                    <th style={{ border: 'none' }}>
                                        Info
                                    </th>
                                    <th style={{ border: 'none' }}>
                                        Action
                                    </th>
                                </tr>
                            </Thead>
                            <Tbody>
                                {[...vars].sort((a, b) => (b.required ? 1 : 0) - (a.required ? 1 : 0)).map((val) => {
                                    const index = vars.indexOf(val)
                                    const infoId = `${infoIdPrefix}-${index}`
                                    return (
                                    <tr key={index}>

                                        <td style={{ border: 'none' }}>
                                            {val.label || val.name}
                                        </td>
                                        <td style={{ border: 'none' }}>
                                            {val.name}
                                        </td>
                                        <td style={{ border: 'none' }}>
                                            {val.type}
                                        </td>
                                        <td style={{ border: 'none' }}>
                                            {val.value}
                                        </td>
                                        <td style={{ border: 'none' }}>
                                            {val.required ? <span className="badge bg-danger">Yes</span> : <span className="badge bg-secondary">No</span>}
                                        </td>
                                        {props.extraColumn && (
                                            <td style={{ border: 'none' }}>
                                                {props.extraColumn.render(val)}
                                            </td>
                                        )}
                                        <td style={{ border: 'none' }}>
                                            {(val.information?.length > 0 || val.notes?.length > 0) && (
                                                <>
                                                    <i className='mdi mdi-information-variant text-primary'
                                                        style={{ fontSize: 'large', cursor: 'pointer' }}
                                                        id={infoId}
                                                    />
                                                    <UncontrolledTooltip
                                                        placement="top"
                                                        target={infoId}
                                                        trigger="hover"
                                                    >
                                                        {val.information?.length > 0 && (
                                                            <span>{formatInfoList(val.information)}</span>
                                                        )}
                                                        {val.information?.length > 0 && val.notes && ' | '}
                                                        {val.notes && <span>{val.notes}</span>}
                                                    </UncontrolledTooltip>
                                                </>
                                            )}
                                        </td>

                                        <td style={{ border: 'none' }}>
                                            <i className="mdi mdi-pencil font-size-16 text-success me-1 pointer" onClick={() => editVar(val, index)} />{" "}
                                            <i className="mdi mdi-trash-can font-size-16 text-danger me-1" onClick={() => removeVar(index)} />

                                        </td>
                                    </tr>
                                    )
                                })}

                            </Tbody>
                        </Table>}

                    {!props.isBase && <Button type="button" className="btn, btn-sm" color="primary" onClick={addVar}>Add</Button>}

                </Col>
            </Row>
        </>
    )
}

export default VarData
